import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export type ReverseVestingCardRow = {
  id: string;
  label: ReactNode;
  value: ReactNode;
};

export type ReverseVestingCardProps = {
  rows: ReverseVestingCardRow[];
  /** Share class of the issuance — the desktop table's own column, folded into the header here. */
  subtitle: ReactNode;
  /** Valuation event of the issuance, or "Company Incorporation". */
  title: ReactNode;
};

/**
 * Phone rendering of one reverse vesting issuance: the desktop row's event and share class become
 * a card header and the remaining columns become label/value rows, so nothing has to scroll
 * sideways on a phone.
 */
export const ReverseVestingCard: FC<ReverseVestingCardProps> = ({ rows, subtitle, title }) => (
  <div className="flex w-full flex-col overflow-hidden rounded-md bg-white shadow-sm">
    <div className="flex h-[72px] w-full items-center border-b border-gray-100 px-3">
      <div className="flex flex-col items-start text-gray-700">
        <span className="text-base font-semibold">{title}</span>
        <span className="text-xs font-[450]">{subtitle}</span>
      </div>
    </div>

    {rows.map(({ id, label, value }, index) => (
      <div
        className={twMerge(
          'flex w-full items-center justify-between gap-2 py-3 pl-3 pr-4 text-sm font-[450] text-gray-700',
          index < rows.length - 1 && 'border-b border-gray-100',
        )}
        key={id}
      >
        <span>{label}</span>
        {value}
      </div>
    ))}
  </div>
);

/** A share count with the share of the schedule it represents, as the phone design shows it. */
export const ReverseVestingCardShares: FC<{ percentage: string; shares: string }> = ({
  percentage,
  shares,
}) => (
  <div className="flex flex-col items-start">
    <span>{shares}</span>
    <span className="text-label-sm font-medium text-gray-500">{percentage}</span>
  </div>
);
